import React from 'react';
import { useTranslation } from 'react-i18next';
import TextTooltipOnHover from '@wix/da-shared-react/pkg/Popover/Preset/TextTooltipOnHover';
import InfoIcon from '@wix/da-ds/pkg/Icons/16x16/Info';
import CheckIcon from '@wix/da-ds/pkg/Icons/24x24/Check';

import s from '../../HeroTableDesktop.scss';

interface Props {
  highlightPackage?: React.ReactNode;
}

const EarningsSection: React.FC<Props> = ({ highlightPackage }) => {
  const { t } = useTranslation();
  const checkIcon = <CheckIcon className={s['check']} disableDefaultColors />;

  return (
    <>
      <div className={s['frame-wrapper']}>
        <div className={s['mid-table']}>
          <div className={s['mid-table-header']}>
            {t('packagePicker.heroTable.middle.header.earnings')}
          </div>
        </div>
      </div>

      <div className={s['frame-wrapper']}>
        {highlightPackage}
        <table className={s['hero-table']}>
          <tbody>
            <tr>
              <th className={s['header-column']}>
                <div className={s['header-layout']}>
                  <div>
                    {t('packagePicker.heroTable.adoptabobbleFees.header')}
                  </div>
                  <TextTooltipOnHover
                    tooltipText={t(
                      'packagePicker.heroTable.adoptabobbleFees.tooltip'
                    )}
                  >
                    <InfoIcon
                      className={s['info']}
                      customSizable
                      disableDefaultColors
                    />
                  </TextTooltipOnHover>
                </div>
              </th>
              <td className={s['core-access']}>{checkIcon}</td>
              <td className={s['core-plus']}>{checkIcon}</td>
              <td className={s['core-pro']}>{checkIcon}</td>
              <td className={s['core-pro-plus']}>{checkIcon}</td>
            </tr>
            <tr>
              <th className={s['header-column']}>
                <div className={s['header-layout']}>
                  <div>
                    {t('packagePicker.heroTable.promotionalBanners.header')}
                  </div>
                  <TextTooltipOnHover
                    tooltipText={t(
                      'packagePicker.heroTable.promotionalBanners.tooltip'
                    )}
                  >
                    <InfoIcon
                      className={s['info']}
                      customSizable
                      disableDefaultColors
                    />
                  </TextTooltipOnHover>
                </div>
              </th>
              <td className={s['core-access']}>{checkIcon}</td>
              <td className={s['core-plus']}>{checkIcon}</td>
              <td className={s['core-pro']}>{checkIcon}</td>
              <td className={s['core-pro-plus']}>{checkIcon}</td>
            </tr>
            <tr>
              <th className={s['header-column']}>
                <div className={s['header-layout']}>
                  <div>
                    {t('packagePicker.heroTable.adoptabobbleResaleFees.header')}
                  </div>
                  <TextTooltipOnHover
                    tooltipText={t(
                      'packagePicker.heroTable.adoptabobbleResaleFees.tooltip'
                    )}
                  >
                    <InfoIcon
                      className={s['info']}
                      customSizable
                      disableDefaultColors
                    />
                  </TextTooltipOnHover>
                </div>
              </th>
              <td className={s['core-access']}>
                {t('packagePicker.heroTable.adoptabobbleFees.coreAccess')}
              </td>
              <td className={s['core-plus']}>
                {t('packagePicker.heroTable.adoptabobbleFees.corePlus')}
              </td>
              <td className={s['core-pro']}>
                {t('packagePicker.heroTable.adoptabobbleFees.corePro')}
              </td>
              <td className={s['core-pro-plus']}>
                {t('packagePicker.heroTable.adoptabobbleFees.coreProPlus')}
              </td>
            </tr>
            <tr>
              <th className={s['header-column']}>
                <div className={s['header-layout']}>
                  <div>
                    {t('packagePicker.heroTable.subscriptionSalesFee.header')}
                  </div>
                  <TextTooltipOnHover
                    tooltipText={t(
                      'packagePicker.heroTable.subscriptionSalesFee.tooltip'
                    )}
                  >
                    <InfoIcon
                      className={s['info']}
                      customSizable
                      disableDefaultColors
                    />
                  </TextTooltipOnHover>
                </div>
              </th>
              <td className={s['core-access']}>
                {t('packagePicker.heroTable.subscriptionSalesFee.coreAccess')}
              </td>
              <td className={s['core-plus']}>
                {t('packagePicker.heroTable.subscriptionSalesFee.corePlus')}
              </td>
              <td className={s['core-pro']}>
                {t('packagePicker.heroTable.subscriptionSalesFee.corePro')}
              </td>
              <td className={s['core-pro-plus']}>
                {t('packagePicker.heroTable.subscriptionSalesFee.coreProPlus')}
              </td>
            </tr>
            <tr>
              <th className={s['header-column']}>
                <div className={s['header-layout']}>
                  <div>{t('packagePicker.heroTable.commissionFee.header')}</div>
                  <TextTooltipOnHover
                    tooltipText={t(
                      'packagePicker.heroTable.commissionFee.tooltip'
                    )}
                  >
                    <InfoIcon
                      className={s['info']}
                      customSizable
                      disableDefaultColors
                    />
                  </TextTooltipOnHover>
                </div>
              </th>
              <td className={s['core-access']}>
                {t('packagePicker.heroTable.commissionFee.coreAccess')}
                <div className={s['dimmed-value']}>
                  {t('packagePicker.heroTable.commissionFee.base')}
                </div>
              </td>
              <td className={s['core-plus']}>
                {t('packagePicker.heroTable.commissionFee.corePlus')}
                <div className={s['dimmed-value']}>
                  {t('packagePicker.heroTable.commissionFee.base')}
                </div>
              </td>
              <td className={s['core-pro']}>
                {t('packagePicker.heroTable.commissionFee.corePro')}
                <div className={s['dimmed-value']}>
                  {t('packagePicker.heroTable.commissionFee.base')}
                </div>
              </td>
              <td className={s['core-pro-plus']}>
                {t('packagePicker.heroTable.commissionFee.coreProPlus')}
                <div className={s['dimmed-value']}>
                  {t('packagePicker.heroTable.commissionFee.base')}
                </div>
              </td>
            </tr>
            <tr>
              <th className={s['header-column']}>
                <div className={s['header-layout']}>
                  <div>{t('packagePicker.heroTable.artDomain.header')}</div>
                  <TextTooltipOnHover
                    tooltipText={t('packagePicker.heroTable.artDomain.tooltip')}
                  >
                    <InfoIcon
                      className={s['info']}
                      customSizable
                      disableDefaultColors
                    />
                  </TextTooltipOnHover>
                </div>
              </th>
              <td className={s['core-access']}>
                {t('packagePicker.heroTable.artDomain.coreAccess')}
              </td>
              <td className={s['core-plus']}>
                {t('packagePicker.heroTable.artDomain.corePlus')}
              </td>
              <td className={s['core-pro']}>
                {t('packagePicker.heroTable.artDomain.corePro')}
              </td>
              <td className={s['core-pro-plus']}>
                {t('packagePicker.heroTable.artDomain.coreProPlus')}
                <div className={s['dimmed-value']}>
                  {t('packagePicker.heroTable.artDomain.yearPlansOnly')}
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </>
  );
};
EarningsSection.displayName = 'EarningsSection';

export default EarningsSection;
